import {Box, Text, Flex, Center} from '@chakra-ui/react'
import {useEffect, useState} from 'react'
import GlobalButton from '@components/GlobalButton/GlobalButton'
import {Widget} from '@typeform/embed-react'
import Lottie from 'lottie-react'
import ratingLottie from '@assets/lotties/rating.json'
import mapRequest from '@apis/map'
import {useDispatch, useSelector} from 'react-redux'
import appRequest from '@apis/app'
import {getFeedbackType, isLoggedIn} from '@utils'
import {getFeedbackOverview} from '@redux/shared/shared'

const colors = [
  '#F13B32',
  '#FF5E00',
  '#F80',
  '#FF9D00',
  '#FFB700',
  '#FBDA06',
  '#F5C929',
  '#8FD328',
  '#69BF3B',
  '#4CAF4F',
]

const Survey = ({
  setIsSurveyed,
  taskSummary,
}: {
  setIsSurveyed: any
  taskSummary: any
}) => {
  const [step, setStep] = useState<number>(1)
  const [isShowTypeForm, setIsShowTypeForm] = useState<boolean>(false)
  const [feedbackData, setFeedbackData] = useState<any>(null)
  const {dataFeedbackOverview} = useSelector((state: any) => state.shared)
  const dispatch: any = useDispatch()

  useEffect(() => {
    if (!taskSummary || !dataFeedbackOverview) return
    getFeedbackData(taskSummary)
  }, [taskSummary, dataFeedbackOverview])

  const getFeedbackData = async (taskSummary: any) => {
    try {
      const taskType = taskSummary?.current_task?.task_type
      if (!taskType) return

      const feedbackType = getFeedbackType(taskType)
      if (!feedbackType) return

      const feedbackId =
        dataFeedbackOverview?.feedback_data[feedbackType]?.feedback_id
      if (!feedbackId) return

      const res = await mapRequest.getFeedbackData({'feedback-id': feedbackId})
      setFeedbackData(res?.data?.data)
    } catch (error) {
      console.error('Error getting feedback data:', error)
    }
  }

  const handleClickRate = async (score: number) => {
    if (feedbackData?.feedback_data?.typeform_id) {
      setStep(2)
    } else {
      setIsSurveyed(true)
    }
    try {
      const feedbackType: any = getFeedbackType(
        taskSummary?.current_task?.task_type,
      )
      let params
      if (isLoggedIn()) {
        params = {
          feedback_data_id:
            dataFeedbackOverview?.feedback_data[feedbackType]?.feedback_id,
          score: score,
          feature: taskSummary?.current_task?.task_type,
        }
      } else {
        params = {
          feedback_data_id:
            dataFeedbackOverview?.feedback_data[feedbackType]?.feedback_id,
          feedback_data: {
            score: score,
            feature: taskSummary?.current_task?.task_type,
          },
        }
      }
      const res = await appRequest.submitFeatureFeedback(params)
      dispatch(getFeedbackOverview())
    } catch (error) {}
  }

  const handleClickSkip = () => {
    setIsSurveyed(true)
  }

  const handleSubmitTypeForm = () => {
    setIsSurveyed(true)
  }

  return (
    <>
      {step === 1 && (
        <Flex
          flexDirection={'column'}
          alignItems={'center'}
          mt={{base: '80px', md: '150px'}}
          px={{md: '0'}}>
          <Box maxW={'100%'}>
            <Text
              align={'center'}
              fontSize={{base: '20px', md: '24px'}}
              fontWeight={600}>
              {feedbackData?.feedback_data?.text}
            </Text>
            <Text align={'center'} mt={'20px'}>
              {feedbackData?.feedback_data?.sub_text}
            </Text>
            <Flex
              mt={'40px'}
              justifyContent={'center'}
              gap={{base: '10px', sm: '20px', md: '32px'}}
              flexWrap={{base: 'wrap', md: 'nowrap'}}>
              {Array.from({
                length: Number(feedbackData?.feedback_data?.total_score),
              }).map((_, index) => (
                <Box
                  key={index}
                  w={{base: '22px', md: '48px'}}
                  h={{base: '22px', md: '48px'}}
                  position={'relative'}
                  mb={{base: '40px', md: '0'}}>
                  <Center
                    bg={colors[index]}
                    cursor={'pointer'}
                    borderRadius={'50%'}
                    fontSize={{base: '14px', md: '24px'}}
                    fontWeight={600}
                    position={'absolute'}
                    w={'100%'}
                    h={'100%'}
                    color={'white'}
                    opacity={0.4}
                    transition={'all 0.3s'}
                    _hover={{
                      opacity: 1,
                      transform: 'scale(1.2)',
                    }}
                    onClick={() => handleClickRate(index + 1)}>
                    {index + 1}
                  </Center>
                  {(index === 0 || index === 9) && (
                    <Text
                      position={'absolute'}
                      bottom={'-40px'}
                      left={'50%'}
                      transform={'translateX(-50%)'}
                      fontWeight={600}
                      fontSize={{base: '12px', md: '14px'}}
                      style={{
                        textWrap: 'nowrap',
                      }}>
                      {index === 0
                        ? feedbackData?.feedback_data?.min
                        : feedbackData?.feedback_data?.max}
                    </Text>
                  )}
                </Box>
              ))}
            </Flex>
          </Box>
        </Flex>
      )}
      {step === 2 && (
        <Flex
          flexDirection={'column'}
          alignItems={'center'}
          mt={{base: '60px', md: '100px'}}
          px={{base: '16px', md: '0'}}>
          <>
            {isShowTypeForm ? (
              <Box className="typeform-iframe" w={'100%'}>
                <Widget
                  id={feedbackData?.feedback_data?.typeform_id}
                  style={{width: '100%', maxWidth: '700px', height: '500px'}}
                  onSubmit={handleSubmitTypeForm}
                />
              </Box>
            ) : (
              <>
                <Box w={{base: '200px', md: '300px'}}>
                  <Lottie animationData={ratingLottie} />
                </Box>
                <Text
                  fontSize={{base: '20px', md: '24px'}}
                  fontWeight={600}
                  textAlign={'center'}>
                  Chúng mình muốn biết ý kiến của bạn
                </Text>
                <Text
                  fontWeight={400}
                  mt={'16px'}
                  maxW={'405px'}
                  textAlign={'center'}
                  px={{base: '16px', md: '0'}}>
                  Mời bạn tham gia khảo sát sau để giúp chúng mình hiểu hơn về
                  nhu cầu học của bạn
                </Text>
                <GlobalButton
                  px={'40px'}
                  py={'22px'}
                  mt={'40px'}
                  borderRadius={'12px'}
                  onClick={() => setIsShowTypeForm(true)}>
                  Làm khảo sát
                </GlobalButton>
                <GlobalButton
                  fontSize={'16px'}
                  fontWeight={600}
                  mt={'16px'}
                  color={'#007EF5'}
                  bg={'transparent'}
                  _hover={{
                    bg: '#e3f2ff',
                  }}
                  onClick={handleClickSkip}>
                  Để sau
                </GlobalButton>
              </>
            )}
          </>
        </Flex>
      )}
    </>
  )
}

export default Survey
